import { useCallback, useEffect, useState, type ChangeEvent, type MouseEvent } from 'react';

import type { TFunction } from 'i18next';

import { IconButton, Tooltip } from '@scaleaq/scaleui';
import { useTranslation } from 'react-i18next';

import { useDeleteSnapshots, useSnapshotFileUrl, useSnapshotHistory } from '@/dataAccess/rest';
import { colors, Icon } from '@/utils';

import {
  StyledFilmstripHost,
  StyledHistoryLabel,
  StyledHistorySlider,
  StyledHistorySliderBox,
  StyledPersistBadge,
  StyledThumbButton,
  StyledThumbColumn,
  StyledViewerActions,
  StyledViewerCaption,
  StyledViewerImage,
  StyledViewerOverlay,
} from './cameraSnapshotsOverlay.styles.ts';
import type { VideoSnapshot } from './useVideoSnapshots.ts';

/** One strip entry — a session capture (has `local`) or an on-cluster history item. */
type StripItem = {
  key: string;
  src: string;
  frameAt: Date;
  offsetFromLiveSeconds?: number;
  local?: VideoSnapshot;
};

type CameraSnapshotsOverlayProps = {
  snapshots: VideoSnapshot[];
  onRemove: (id: string) => void;
  /** Retry the on-cluster upload for a capture whose automatic upload failed. */
  onPersist: (id: string) => void;
  /** Used in downloaded filenames: `<cameraName>_<timestamp>_-<offset>s.jpg`. */
  cameraName: string;
  /** History lookups are keyed by `{cameraId}` on the server (SCO-2405); tier is per-item metadata. */
  cameraId: string;
  /**
   * Render at all. DVR-rewind ONLY: nothing of this component is shown over a live feed —
   * no strip, and no viewer. Live captures are stored in the background and become
   * visible here as soon as the operator enters rewind. A dedicated grid view is the
   * planned home for full browsing.
   */
  historyEnabled: boolean;
  zIndex: number;
};

const PAGE_SIZE = 12;

/**
 * Snapshot filmstrip (SCO-2290 strip, SCO-2405 history), shown in **DVR rewind only**.
 * Thumbnails stack along the LEFT edge, vertically centred; hidden until its own area is
 * hovered. The strip shows one page (12) at a time.
 *
 * Content is this session's captures — live and DVR alike, since every capture is stored
 * on the cluster in the background — merged with the newest stored page for the same
 * `{cameraId}`, so snapshots from earlier sessions are there straight after a page load,
 * both tiers mixed together; the slider underneath pages back through everything on
 * cluster storage.
 *
 * Clicking a thumbnail opens the full-size viewer, which is the ONLY place download,
 * delete and upload-retry are offered. Capturing over the live feed opens nothing.
 */
export const CameraSnapshotsOverlay = ({
  snapshots,
  onRemove,
  onPersist,
  cameraName,
  cameraId,
  historyEnabled,
  zIndex,
}: CameraSnapshotsOverlayProps) => {
  const { t } = useTranslation('cameraControl');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  /** null ⇒ live session strip; a UTC ms value ⇒ browsing stored history at that time. */
  const [historyCursorMs, setHistoryCursorMs] = useState<number | null>(null);

  const toFileUrl = useSnapshotFileUrl();
  const { mutateAsync: deleteStoredSnapshots } = useDeleteSnapshots();
  const { data: page, isSuccess: historyLoaded } = useSnapshotHistory({
    cameraId,
    // '+1s' makes the exclusive server cursor include the second the slider points at.
    before: historyCursorMs !== null ? new Date(historyCursorMs + 1000).toISOString() : undefined,
    limit: PAGE_SIZE,
    enabled: historyEnabled && Boolean(cameraId),
  });

  const browsingHistory = historyCursorMs !== null;
  // Paths whose DELETE is in flight (or done but not yet refetched). Hidden optimistically
  // so a deleted image can't reappear as a history thumb; restored if the DELETE fails,
  // which is truthful — the file is still on cluster storage.
  const [pendingDeletePaths, setPendingDeletePaths] = useState<ReadonlySet<string>>(
    () => new Set()
  );
  const storedItems: StripItem[] = (page?.items ?? [])
    .filter(remote => !pendingDeletePaths.has(remote.path))
    .map(remote => ({
      key: remote.path,
      src: toFileUrl(remote.url),
      frameAt: new Date(remote.frameAtUtc),
    }));
  // The widget is not remounted when the operator switches camera, so the session list
  // can hold captures from a previous camera — show only this one's, both tiers. Captures
  // for another camera stay in memory and reappear when the operator switches back.
  const sessionSnapshots = snapshots.filter(snapshot => snapshot.cameraId === cameraId);
  const sessionItems: StripItem[] = sessionSnapshots.map(snapshot => ({
    key: snapshot.id,
    src: snapshot.url,
    frameAt: snapshot.frameAt,
    offsetFromLiveSeconds: snapshot.offsetFromLiveSeconds,
    local: snapshot,
  }));

  // At the live edge of the timeline the strip shows this session's captures merged with
  // the newest stored page, so snapshots from EARLIER sessions are visible immediately
  // (a fresh page load would otherwise show an empty strip). A session capture that has
  // been persisted is the same image as its stored copy, so drop the duplicate.
  const persistedPaths = new Set(
    sessionSnapshots
      .map(snapshot => snapshot.remotePath)
      .filter((path): path is string => Boolean(path))
  );
  const items: StripItem[] = browsingHistory
    ? storedItems
    : [...sessionItems, ...storedItems.filter(item => !persistedPaths.has(item.key))]
        .sort((a, b) => b.frameAt.getTime() - a.frameAt.getTime())
        .slice(0, PAGE_SIZE);

  const selected = items.find(item => item.key === selectedKey) ?? null;

  // Switching camera changes which `{cameraId}` history is being browsed, so a
  // cursor/selection from the previous camera is meaningless — start at the newest.
  useEffect(() => {
    setHistoryCursorMs(null);
    setSelectedKey(null);
  }, [/* effect dep */ cameraId]);

  // A cursor can point at a window that holds nothing (deleted, or swept by retention).
  // The strip would then render empty and unmount itself, stranding the cursor with no
  // slider left to drag back — so fall back to the newest view.
  const historyPageIsEmpty = browsingHistory && historyLoaded && page?.items.length === 0;
  useEffect(() => {
    if (historyPageIsEmpty) setHistoryCursorMs(null);
  }, [historyPageIsEmpty]);

  const oldestMs = page?.oldestUtc ? new Date(page.oldestUtc).getTime() : null;
  const newestMs = page?.newestUtc ? new Date(page.newestUtc).getTime() : null;
  // Show the slider whenever stored snapshots exist that the strip is NOT currently
  // displaying. Comparing against the visible stored count (rather than PAGE_SIZE) also
  // covers the merged view, where session captures occupy slots and can push older
  // stored thumbs out of the window. A PERSISTED session capture counts as stored — it is
  // on screen and its stored twin was deduped away — otherwise the slider offers to
  // reveal history that is already visible.
  const storedShownCount = items.filter(
    item => !item.local || Boolean(item.local.remotePath)
  ).length;
  const showSlider =
    oldestMs !== null &&
    newestMs !== null &&
    oldestMs < newestMs &&
    (browsingHistory || (page?.total ?? 0) > storedShownCount);

  const handleSliderChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = Number(event.target.value);
      setSelectedKey(null);
      // Right edge = back to the live session strip.
      setHistoryCursorMs(newestMs !== null && value >= newestMs ? null : value);
    },
    [newestMs]
  );

  const handleDownload = useCallback(
    async (item: StripItem) => {
      // Session captures download straight from their object URL; history items are
      // fetched to a blob first (the download attribute is ignored cross-origin).
      const href = item.local
        ? item.src
        : URL.createObjectURL(await fetch(item.src).then(response => response.blob()));
      const anchor = document.createElement('a');
      anchor.href = href;
      anchor.download = snapshotFilename(cameraName, item);
      anchor.click();
      if (!item.local) URL.revokeObjectURL(href);
    },
    [cameraName]
  );

  /**
   * Delete removes the image everywhere it exists: the stored copy on cluster storage
   * (session capture that was persisted, or a history item) AND the in-session entry.
   * The server skips unknown paths, so a failed/never-uploaded capture just drops locally.
   */
  const handleDelete = useCallback(
    async (item: StripItem) => {
      setSelectedKey(null);
      const storedPath = item.local ? item.local.remotePath : item.key;
      if (item.local) onRemove(item.local.id);
      if (!storedPath) return;

      setPendingDeletePaths(previous => new Set(previous).add(storedPath));
      try {
        await deleteStoredSnapshots({ paths: [storedPath] });
      } catch (error) {
        // Un-hide: the file is still stored, so showing it again is correct.
        setPendingDeletePaths(previous => {
          const next = new Set(previous);
          next.delete(storedPath);
          return next;
        });
        console.warn('Snapshot delete failed:', error);
      }
    },
    [onRemove, deleteStoredSnapshots]
  );

  // DVR rewind only. A live capture stores in the background and surfaces here the
  // next time the operator rewinds — it never opens a viewer over the live feed.
  if (!historyEnabled) return null;
  if (!items.length) return null;

  return (
    <>
      <StyledFilmstripHost
        $zIndex={zIndex}
        onClick={stopSurfaceEvents}
        onDoubleClick={stopSurfaceEvents}
      >
        <StyledThumbColumn>
          {items.map(item => (
            <StyledThumbButton
              key={item.key}
              type='button'
              aria-label={t('snapshot.view', { time: formatFrameTime(item.frameAt) })}
              title={thumbTitle(item, t)}
              onClick={() => setSelectedKey(item.key)}
            >
              <img src={item.src} alt={formatFrameTime(item.frameAt)} />
              {item.local && item.local.persistState !== 'saved' && (
                <StyledPersistBadge $state={item.local.persistState} />
              )}
            </StyledThumbButton>
          ))}
        </StyledThumbColumn>

        {showSlider && (
          <StyledHistorySliderBox>
            <StyledHistorySlider
              type='range'
              min={oldestMs}
              max={newestMs}
              step={1000}
              value={historyCursorMs ?? newestMs}
              aria-label={t('snapshot.historySlider')}
              onChange={handleSliderChange}
            />
            <StyledHistoryLabel>
              {browsingHistory ? new Date(historyCursorMs).toLocaleString() : t('snapshot.latest')}
            </StyledHistoryLabel>
          </StyledHistorySliderBox>
        )}
      </StyledFilmstripHost>

      {selected && (
        <StyledViewerOverlay
          $zIndex={zIndex + 1}
          onClick={event => {
            stopSurfaceEvents(event);
            setSelectedKey(null);
          }}
          onDoubleClick={stopSurfaceEvents}
        >
          <StyledViewerImage
            src={selected.src}
            alt={formatFrameTime(selected.frameAt)}
            onClick={stopSurfaceEvents}
          />
          <StyledViewerActions onClick={stopSurfaceEvents}>
            {/* Upload to cluster storage — only for captures that are session-only
                ('local'), i.e. the automatic store failed. */}
            {canUpload(selected) && (
              <Tooltip tooltip={t('snapshot.upload')} placement='bottom'>
                <IconButton
                  aria-label={t('snapshot.upload')}
                  variant='soft'
                  size='large'
                  Icon={<Icon.CloudUpload size='large' color={colors.icon.action[100]} />}
                  onClick={() => selected.local && onPersist(selected.local.id)}
                />
              </Tooltip>
            )}
            {selected.local?.persistState === 'saving' && (
              <Tooltip tooltip={t('snapshot.saving')} placement='bottom'>
                <IconButton
                  aria-label={t('snapshot.saving')}
                  variant='soft'
                  size='large'
                  disabled
                  Icon={<Icon.CloudUpload size='large' color={colors.icon.neutral[100]} />}
                />
              </Tooltip>
            )}
            <Tooltip tooltip={t('snapshot.download')} placement='bottom'>
              <IconButton
                aria-label={t('snapshot.download')}
                variant='soft'
                size='large'
                Icon={<Icon.FileDown size='large' color={colors.icon.action[100]} />}
                onClick={() => void handleDownload(selected)}
              />
            </Tooltip>
            <Tooltip tooltip={t('snapshot.delete')} placement='bottom'>
              <IconButton
                aria-label={t('snapshot.delete')}
                variant='soft'
                size='large'
                Icon={<Icon.Trash2 size='large' color={colors.icon.neutral[100]} />}
                onClick={() => void handleDelete(selected)}
              />
            </Tooltip>
            <Tooltip tooltip={t('snapshot.close')} placement='bottom-end'>
              <IconButton
                aria-label={t('snapshot.close')}
                variant='soft'
                size='large'
                Icon={<Icon.X size='large' color={colors.icon.neutral[100]} />}
                onClick={() => setSelectedKey(null)}
              />
            </Tooltip>
          </StyledViewerActions>
          <StyledViewerCaption>{viewerCaption(selected, t)}</StyledViewerCaption>
        </StyledViewerOverlay>
      )}
    </>
  );
};

//*********************************
// Helpers
//*********************************
type Translate = TFunction<'cameraControl'>;

/** Keep clicks/double-clicks on snapshot UI from reaching the widget surface
    (fullscreen toggle, camera focus, context menu). */
const stopSurfaceEvents = (event: MouseEvent) => event.stopPropagation();

/** Date and time: stored snapshots span days, so the time alone is ambiguous. */
const formatFrameTime = (frameAt: Date): string => frameAt.toLocaleString();

/** Not on the cluster (yet): a live grab awaiting upload, or a failed DVR upload. */
const canUpload = (item: StripItem): boolean => item.local?.persistState === 'local';

const thumbTitle = (item: StripItem, t: Translate): string => {
  const time = formatFrameTime(item.frameAt);
  if (item.local?.persistState === 'saving') return `${time} — ${t('snapshot.saving')}`;
  if (item.local?.persistState === 'local') return `${time} — ${t('snapshot.notUploaded')}`;
  return time;
};

const viewerCaption = (item: StripItem, t: Translate): string => {
  const offset = item.offsetFromLiveSeconds ?? 0;
  const base =
    offset > 0 ? `${formatFrameTime(item.frameAt)} (-${offset}s)` : formatFrameTime(item.frameAt);
  if (item.local?.persistState === 'local') return `${base} — ${t('snapshot.notUploaded')}`;
  return base;
};

const snapshotFilename = (cameraName: string, item: StripItem): string => {
  const timestamp = item.frameAt.toISOString().replace(/[:.]/g, '-');
  const offset = item.offsetFromLiveSeconds ? `_-${item.offsetFromLiveSeconds}s` : '';
  return `${cameraName}_${timestamp}${offset}.jpg`;
};
